import { useMemo } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { CustomHeaderCell } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/CustomHeaderCell';

import { MODULE_KEYS_USER_PREFERENCE_PERSISTENCE } from 'constants/ProjectConstants';
import { getPersistenceKey } from 'constants/helper';

import { AgGridTable } from 'uiToolkitV1/AgGridWrapperV2';
import { Avatar } from 'uiToolkitV1/Avatar';
import { ListItemComp } from 'uiToolkitV1/ListItemComp';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import { getRelevancyScoreColorLifecycle } from './CandidateProgressTable';
import { TABLE_ROWS_ONBOARD, TABLE_COLUMNS_ONBOARD, TABLE_COLUMNS_DISPLAYNAME_ONBOARD } from './constants';

const getSkillsJSX = (value) => (
  <Flex alignItems="flex-start" wrap="wrap">
    {value.map((skill) => {
      const { name: skillName, color } = skill;
      return (
        <Box
          sx={{
            textAlign: 'center',
            fontSize: '12px',
            fontStyle: 'normal',
            fontWeight: 590,
            bgColor: `${color}30`,
            gap: 4,
            margin: '2px',
            padding: '4px 8px',
            borderRadius: '4px',
          }}
        >
          <TextLabel color={color}>{skillName}</TextLabel>
        </Box>
      );
    })}
  </Flex>
);

function getCellContent(displayName, cellData) {
  const { value } = cellData;
  switch (displayName) {
    case TABLE_COLUMNS_DISPLAYNAME_ONBOARD.MENTOR_NAME:
    case TABLE_COLUMNS_DISPLAYNAME_ONBOARD.EMPLOYEE_NAME: {
      const { name, profilePhoto, email = '' } = value;
      return (
        <Box className="info">
          <ListItemComp
            title={name}
            // eslint-disable-next-line react/no-unstable-nested-components
            LeftComponent={() => <Avatar src={profilePhoto} name="candidate" />}
            isBoldText={false}
            withoutBackground
            subTitle={email}
          />
        </Box>
      );
    }

    case TABLE_COLUMNS_DISPLAYNAME_ONBOARD.FUNCTION:
    case TABLE_COLUMNS_DISPLAYNAME_ONBOARD.NUMBER_OF_MENTEES:
    case TABLE_COLUMNS_DISPLAYNAME_ONBOARD.ROLE: {
      return (
        <Box className="info">
          <TextBodyMedium>{value}</TextBodyMedium>
        </Box>
      );
    }

    case TABLE_COLUMNS_DISPLAYNAME_ONBOARD.MENTOR_MATCH: {
      return (
        <Box className="info">
          <TextBodyMedium bold color={getRelevancyScoreColorLifecycle(value)}>
            {value}
          </TextBodyMedium>
        </Box>
      );
    }

    case TABLE_COLUMNS_DISPLAYNAME_ONBOARD.SKILLS: {
      return getSkillsJSX(value);
    }

    default: {
      return new Error('Invalid column name', displayName);
    }
  }
}

function CellRenderer(params) {
  const cellInfo = params.colDef;
  const { displayName, colId } = cellInfo;
  const cellData = params?.data?.[colId];
  return getCellContent(displayName, cellData);
}

function getColProps() {
  const defaultColProps = {
    menuTabs: ['filterMenuTab', 'generalMenuTab'],
    headerComponent: CustomHeaderCell,
  };
  return {
    ...defaultColProps,
    sortable: true,
    valueGetter: (params) => params?.data[params.colDef.id]?.value,
    cellRenderer: CellRenderer,
    filter: 'agTextColumnFilter',
  };
}

const CandidatesOnboardTable = ({ gridRef }) => {
  const newColumnDefs = useMemo(
    () =>
      TABLE_COLUMNS_ONBOARD.map((col) => ({
        colId: col.id,
        field: col.id,
        id: col.id,
        displayName: col.displayName,
        minWidth: col.displayName === 'Skills' ? 500 : 220,
        hide: false,
        flex: 1,
        headerComponent: CustomHeaderCell,
        headerComponentParams: {
          enableMenu: true,
          enableSorting: true,
        },
        width: col.displayName === 'Skills' ? 500 : 220,
        headerValueGetter: () => col.displayName,
        wrapText: true,
        autoHeight: true,
        ...getColProps(),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [TABLE_COLUMNS_ONBOARD]
  );

  const gridStatePersistenceKey = useMemo(() => {
    const moduleName = MODULE_KEYS_USER_PREFERENCE_PERSISTENCE.REVIEW;
    const viewId = 'LifecycleInsights';
    const tabId = 'Explore';
    const uniqueId = 'LifecycleInsightsGrid';

    return getPersistenceKey({
      moduleName,
      viewId,
      tabId,
      uniqueId,
    });
  }, []);

  const sideBar = useMemo(
    () => ({
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressColumnFilter: false,
            suppressColumnSelectAll: true,
            suppressColumnExpandAll: true,
          },
        },
      ],
      defaultToolPanel: '',
    }),
    []
  );

  const handleCellClick = ({ data, column }) => {
    console.log('option clicked', data, column);
  };

  return (
    <Box className="review">
      <AgGridTable
        // defaultColDef={defaultColDef}
        ref={gridRef}
        gridStatePersistenceKey={gridStatePersistenceKey}
        sideBar={sideBar}
        columnDefs={newColumnDefs}
        rowData={TABLE_ROWS_ONBOARD}
        kebabMenuIsRowSpecific={false}
        domLayout="autoHeight"
        animateRows={false}
        rowHeight={150}
        containerAutoHeight
        pagination
        suppressPaginationPanel
        paginationPageSize={10}
        showSidebarToggle={false}
        onCellClicked={handleCellClick}
      />
    </Box>
  );
};

export { CandidatesOnboardTable };
